import FeedbackDetails from '@components/Feedback/FeedbackDetails'
import FormikSelectField from '@dashboard-components/Formik/FormikSelectField'
import { FeedbackListQuery } from '@graphql/queries/__generated__/feedback.generated'
import { MarketplacesQuery } from '@graphql/queries/__generated__/marketplace.generated'
import { useTheme } from '@mui/material'
import Grid from '@mui/material/Grid'
import Pagination from '@mui/material/Pagination'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Form, Formik } from 'formik'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

export interface IFeedbackFormData {
  score: number
  marketplace: string
}

interface IProps {
  feedbacks: FeedbackListQuery['feedbackList'] | null
  marketplaces: MarketplacesQuery['marketplaces']['edges']
  page: number
  paginateBy: number

  changePage(page: number): void
  onSubmit(values: IFeedbackFormData): void
}

const FeedbackPage: React.FC<IProps> = ({ feedbacks, marketplaces, page, paginateBy, changePage, onSubmit }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <div className='px-5'>
      <h1 className='text-xl sm:text-5xl text-center sm:text-left tracking-3/2 font-semibold uppercase mb-12'>
        {t('customersFeedback', "Customer's feedback")}
      </h1>
      <Formik<IFeedbackFormData>
        initialValues={{
          score: 0,
          marketplace: '',
        }}
        onSubmit={onSubmit}
      >
        {form => {
          useEffect(() => {
            form.handleSubmit()
          }, [form.values])
          return (
            <Form className='mb-10'>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <FormikSelectField
                    name='score'
                    variant='outlined'
                    displayEmpty
                    options={[
                      { value: 0, label: t('formOptions.all', 'All') },
                      { value: 5, label: t('formOptions.feedbackPositive', 'Positive') },
                      { value: 3, label: t('formOptions.feedbackNeutral', 'Neutral') },
                      { value: 1, label: t('formOptions.feedbackNegative', 'Negative') },
                    ]}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormikSelectField
                    name='marketplace'
                    variant='outlined'
                    displayEmpty
                    options={[
                      { value: '', label: t('formOptions.allMarketplaces', 'All marketplaces') },
                      ...marketplaces.map(m => ({
                        value: m.node.pk,
                        label: m.node.name,
                      })),
                    ]}
                  />
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
      <div className='feedbacks'>
        {feedbacks?.results?.map(feedback => (
          <FeedbackDetails key={feedback.pk} feedback={feedback} />
        ))}
      </div>
      <div className='paginator'>
        {Math.ceil((feedbacks?.totalCount || 0) / paginateBy) > 1 && (
          <Pagination
            count={Math.ceil((feedbacks?.totalCount || 0) / paginateBy)}
            page={page}
            onChange={(e, val) => {
              changePage(val)
            }}
            siblingCount={smDown ? 1 : 4}
            shape='rounded'
          />
        )}
      </div>
    </div>
  )
}

export default FeedbackPage
